import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  HStack,
  Badge,
  Button,
  Flex,
  Spacer,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  VStack,
} from "@chakra-ui/react";
import {
  MdSearch,
  MdFilterList,
  MdVisibility,
  MdRefresh,
  MdAdd,
} from "react-icons/md";
import { useBatchStats, useBatchOperations } from "../hooks/useBatches";
import { handleApiError, handleApiSuccess } from "../utils/apiHelpers";
import { batchesAPI } from "../services/api/batchesAPI";

const BatchRow = ({ batch, isSelected, onToggle }) => {
  const getStatusColor = (value) => {
    if (!value || value === 0) return "gray.500";
    return "gray.300";
  };

  const formatValue = (value) => {
    return value || value === 0 ? value : "-";
  };

  return (
    <Tr bg="#121212" borderColor="#3A3A3A" _hover={{ bg: "#1A1A1A" }}>
      <Td borderColor="#3A3A3A" py="4">
        <Checkbox
          isChecked={isSelected}
          onChange={onToggle}
          colorScheme="orange"
        />
      </Td>
      <Td borderColor="#3A3A3A" color="white" fontWeight="medium">
        {batch.batch_name || "Unknown Batch"}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={getStatusColor(batch.total_models)}
        textAlign="center"
      >
        {formatValue(batch.total_models)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={getStatusColor(batch.not_started)}
        textAlign="center"
      >
        {formatValue(batch.not_started)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={
          batch.high_quality > 0
            ? "green.400"
            : getStatusColor(batch.high_quality)
        }
        textAlign="center"
      >
        {formatValue(batch.high_quality)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={getStatusColor(batch.partial)}
        textAlign="center"
      >
        {formatValue(batch.partial)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={getStatusColor(batch.re_texture)}
        textAlign="center"
      >
        {formatValue(batch.re_texture)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={getStatusColor(batch.dont_keep)}
        textAlign="center"
      >
        {formatValue(batch.dont_keep)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={
          batch.environment > 0 ? "blue.400" : getStatusColor(batch.environment)
        }
        textAlign="center"
      >
        {formatValue(batch.environment)}
      </Td>
      <Td
        borderColor="#3A3A3A"
        color={
          batch.aligned_count > 0
            ? "blue.400"
            : getStatusColor(batch.aligned_count)
        }
        textAlign="center"
      >
        {formatValue(batch.aligned_count)}
      </Td>

      <Td
        borderColor="#3A3A3A"
        color={
          batch.Verified_Models > 0
            ? "gray.300"
            : getStatusColor(batch.Verified_Models)
        }
        textAlign="center"
      >
        {formatValue(batch.Verified_Models)}
      </Td>
      <Td borderColor="#3A3A3A">
        <IconButton
          aria-label="View batch details"
          icon={<MdVisibility />}
          size="sm"
          variant="ghost"
          colorScheme="orange"
          color="gray.400"
          _hover={{ color: "#FF9900", bg: "gray.700" }}
        />
      </Td>
    </Tr>
  );
};

const Batches = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  const [batchName, setBatchName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const fileInputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Get user role for conditional rendering
  const userRole = localStorage.getItem("user_role");
  const isAdmin = userRole === "Admin";

  // Use our custom hooks for API calls
  const { batches, loading, error, refetch } = useBatchStats();
  const {
    updateBatch,
    deleteBatch,
    loading: operationLoading,
  } = useBatchOperations();

  const handleSelectBatch = (batchName) => {
    const newSelected = new Set(selectedBatches);
    if (newSelected.has(batchName)) {
      newSelected.delete(batchName);
    } else {
      newSelected.add(batchName);
    }
    setSelectedBatches(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedBatches.size === batches.length) {
      setSelectedBatches(new Set());
    } else {
      setSelectedBatches(new Set(batches.map((batch) => batch.batch_name)));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type === "application/json" || file.name.endsWith(".json")) {
        setSelectedFile(file);
      } else {
        handleApiError(new Error("Please select a valid JSON file"));
        event.target.value = ""; // Clear the input
      }
    }
  };

  const handleCreateBatch = async () => {
    if (!batchName.trim()) {
      handleApiError(new Error("Please enter a batch name"));
      return;
    }

    if (!selectedFile) {
      handleApiError(new Error("Please select a JSON file"));
      return;
    }

    setIsCreating(true);
    try {
      const response = await batchesAPI.addBatch(
        batchName.trim(),
        selectedFile
      );

      // Check for error in the response
      if (response && response.hasError) {
        throw new Error("Failed to create batch");
      }

      // Check for specific error fields that backend might return
      if (response && response.error) {
        throw new Error("Failed to create batch");
      }

      // Success case
      if (response && response.message) {
        handleApiSuccess(response.message);
      } else {
        handleApiSuccess("Batch created successfully!");
      }

      // Reset form
      setBatchName("");
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Close dialog and refresh data
      onClose();
      refetch();
    } catch (error) {
      console.error("Error creating batch:", error);

      // Handle specific error responses from backend
      let errorMessage = "Failed to create batch";

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      handleApiError(new Error("Failed to create batch: "));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloseDialog = () => {
    // Reset form when closing
    setBatchName("");
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const filteredBatches = batches.filter((batch) =>
    batch.batch_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading spinner
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" color="orange.500" />
      </Box>
    );
  }

  // Show error message
  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Failed to load batch data: {error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}

      <Box mb="6">
        <Flex align="center" justify="space-between">
          <Box w="full" align="center">
            <Text fontSize="3xl" fontWeight="bold" color="white" mb="2">
              Batch Management
            </Text>
            <Text color="gray.400" fontSize="md">
              Manage and verify 3D model batches with sortable columns
            </Text>
          </Box>
          {isAdmin && (
            <Button
              leftIcon={<MdAdd />}
              colorScheme="orange"
              bg="#FF9900"
              color="black"
              onClick={onOpen}
              _hover={{ bg: "#E68A00" }}
            >
              Create Batch
            </Button>
          )}
        </Flex>
      </Box>

      {/* Summary Statistics */}
      {batches.length > 0 && (
        <Box mb="6">
          <HStack spacing="6" wrap="wrap">
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Total Batches
              </Text>
              <Text color="white" fontSize="2xl" fontWeight="bold">
                {batches.length}
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Total Models
              </Text>
              <Text color="white" fontSize="2xl" fontWeight="bold">
                {batches.reduce(
                  (sum, batch) => sum + (batch.total_models || 0),
                  0
                )}
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                High Quality
              </Text>
              <Text color="green.400" fontSize="2xl" fontWeight="bold">
                {batches.reduce(
                  (sum, batch) => sum + (batch.high_quality || 0),
                  0
                )}
              </Text>
            </Box>
            <Box
              bg="#121212"
              p="4"
              borderRadius="md"
              border="1px solid #3A3A3A"
              minW="150px"
            >
              <Text color="gray.400" fontSize="sm">
                Not Started
              </Text>
              <Text color="orange.400" fontSize="2xl" fontWeight="bold">
                {batches.reduce(
                  (sum, batch) => sum + (batch.not_started || 0),
                  0
                )}
              </Text>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Search and Filter Bar */}
      <Flex mb="6" gap="4" align="center">
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <MdSearch color="#666" />
          </InputLeftElement>
          <Input
            placeholder="Search Batch"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="#121212"
            borderColor="#3A3A3A"
            color="white"
            _placeholder={{ color: "gray.500" }}
            _hover={{ borderColor: "#FF9900" }}
            _focus={{
              borderColor: "#FF9900",
              boxShadow: "0 0 0 1px #FF9900",
            }}
          />
        </InputGroup>
        <Spacer />
        <Button
          leftIcon={<MdRefresh />}
          variant="outline"
          colorScheme="orange"
          borderColor="#3A3A3A"
          color="gray.300"
          onClick={() => {
            refetch();
            handleApiSuccess("Batch data refreshed");
          }}
          isLoading={loading}
          _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
        >
          Refresh
        </Button>
        <Button
          leftIcon={<MdFilterList />}
          variant="outline"
          colorScheme="orange"
          borderColor="#3A3A3A"
          color="gray.300"
          _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
        >
          Filter
        </Button>
      </Flex>

      {/* Batches Table */}
      <Box
        bg="#121212"
        borderRadius="lg"
        borderColor="#3A3A3A"
        borderWidth="1px"
        overflow="hidden"
      >
        <Table variant="simple" size="md">
          <Thead bg="#1A1A1A">
            <Tr>
              <Th borderColor="#3A3A3A" py="4">
                <Checkbox
                  isChecked={
                    selectedBatches.size === batches.length &&
                    batches.length > 0
                  }
                  isIndeterminate={
                    selectedBatches.size > 0 &&
                    selectedBatches.size < batches.length
                  }
                  onChange={handleSelectAll}
                  colorScheme="orange"
                />
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
              >
                BATCH NAME
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                TOTAL MODELS
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Box>
                  <Text>CLASSIFICATION</Text>
                  <Text mt="1" fontSize="xs" color="gray.500">
                    NOT STARTED
                  </Text>
                </Box>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>HIGH QUALITY</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>PARTIAL</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>RE-TEXTURE</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>DON'T KEEP</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>ENVIRONMENT</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>Alignment</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>VERIFIED</Text>
              </Th>
              <Th
                borderColor="#3A3A3A"
                color="gray.300"
                fontSize="xs"
                fontWeight="semibold"
                textAlign="center"
              >
                <Text>ACTIONS</Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredBatches.length > 0 ? (
              filteredBatches.map((batch, index) => (
                <BatchRow
                  key={batch.batch_name || index}
                  batch={batch}
                  isSelected={selectedBatches.has(batch.batch_name)}
                  onToggle={() => handleSelectBatch(batch.batch_name)}
                />
              ))
            ) : (
              <Tr>
                <Td colSpan="13" textAlign="center" py="8">
                  <Text color="gray.400" fontSize="lg">
                    {searchTerm
                      ? "No batches found matching your search"
                      : "No batches available"}
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Footer Info */}
      {selectedBatches.size > 0 && (
        <Box mt="4">
          <Text color="gray.400" fontSize="sm">
            {selectedBatches.size} batch
            {selectedBatches.size !== 1 ? "es" : ""} selected
          </Text>
        </Box>
      )}

      {/* Create Batch Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseDialog} isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg="#121212" borderColor="#3A3A3A" border="1px solid">
          <ModalHeader color="white">Create New Batch</ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing="4" align="stretch">
              <FormControl>
                <FormLabel color="gray.300">Batch Name</FormLabel>
                <Input
                  placeholder="Enter batch name"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  bg="#1A1A1A"
                  borderColor="#3A3A3A"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{ borderColor: "#FF9900" }}
                  _focus={{
                    borderColor: "#FF9900",
                    boxShadow: "0 0 0 1px #FF9900",
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">JSON File</FormLabel>
                <Input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  bg="#1A1A1A"
                  borderColor="#3A3A3A"
                  color="white"
                  _hover={{ borderColor: "#FF9900" }}
                  _focus={{
                    borderColor: "#FF9900",
                    boxShadow: "0 0 0 1px #FF9900",
                  }}
                  sx={{
                    "::file-selector-button": {
                      bg: "#FF9900",
                      color: "black",
                      border: "none",
                      borderRadius: "md",
                      mr: "3",
                      px: "3",
                      py: "1",
                      fontSize: "sm",
                      cursor: "pointer",
                      _hover: {
                        bg: "#E68A00",
                      },
                    },
                  }}
                />
                {selectedFile && (
                  <Text color="green.400" fontSize="sm" mt="2">
                    Selected: {selectedFile.name}
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={handleCloseDialog}
              borderColor="#3A3A3A"
              color="gray.300"
              _hover={{ bg: "gray.700" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              bg="#FF9900"
              color="black"
              onClick={handleCreateBatch}
              isLoading={isCreating}
              loadingText="Creating..."
              _hover={{ bg: "#E68A00" }}
              isDisabled={!batchName.trim() || !selectedFile}
            >
              Create Batch
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Batches;
